const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-script';
const GOOGLE_MAPS_CALLBACK = '__initGoogleMaps__';

const DEFAULT_LIBRARIES = ['geometry', 'places'] as const;

let loadPromise: Promise<any> | null = null;

const getApiKey = () => process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

type LoadGoogleMapsOptions = {
  libraries?: string[];
};

declare global {
  interface Window {
    google: any;
    __initGoogleMaps__?: () => void;
  }
}

const buildLibrariesParam = (extraLibraries: string[] = []) => {
  const librarySet = new Set<string>(DEFAULT_LIBRARIES);
  extraLibraries.forEach((lib) => {
    if (lib) {
      librarySet.add(lib);
    }
  });
  return Array.from(librarySet).join(',');
};

export const loadGoogleMaps = ({ libraries = [] }: LoadGoogleMapsOptions = {}) => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps is only available in the browser environment.'));
  }

  if (window.google && window.google.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (loadPromise) {
    return loadPromise;
  }

  const apiKey = getApiKey();
  if (!apiKey) {
    return Promise.reject(
      new Error('Google Maps API key not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY.'),
    );
  }

  loadPromise = new Promise<any>((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${GOOGLE_MAPS_CALLBACK}&libraries=${buildLibrariesParam(
      libraries,
    )}`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = 'true';

    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Google Maps script.'));
    };

    (window as any)[GOOGLE_MAPS_CALLBACK] = () => {
  resolve(window.google!.maps);
      delete (window as any)[GOOGLE_MAPS_CALLBACK];
    };

    document.head.appendChild(script);
  });

  return loadPromise;
};
